import React from 'react';
import toArray from 'utils/to-array';
import is from 'utils/is';
import type { StyledElement, AriaAttributes, TestAutomation } from 'contracts';
import withGATracking from 'enhancers/with-ga-tracking';

import Html from '../html';
import Shimmer from '../shimmer';
import Icon, { IconList } from '../icon';

import styles from './expandable-row.module.scss';

interface ExpandableRowElement extends StyledElement, AriaAttributes, TestAutomation {
  children?: React.ReactNode;
  title: React.ReactElement | string;
  description?: React.ReactElement | string;
  addon?: React.ReactElement | string | null;
  addonClassName?: StyledElement['className'];
  expanded: boolean;
  toggleable?: boolean;
  loading?: boolean;
  onToggle?: () => void;
  canSelfToggle?: boolean;
  /**
   * Allows you to override the toggle icon to be displayed when expanded or collapsed.
   * default is open: caretUp, closed: caretDown
   */
  toggleIcon?: {
    open: IconList;
    closed: IconList;
  };
}

const ExpandableRow: React.FunctionComponent<ExpandableRowElement> = (props) => {
  const {
    testId,
    className,
    style,
    children,
    title,
    description,
    addon,
    addonClassName,
    expanded,
    toggleable = true,
    loading,
    onToggle,
    canSelfToggle,
    toggleIcon = {
      open: IconList.caretUp,
      closed: IconList.caretDown,
    },
    ...rest
  } = props;
  const [isExpanded, setIsExpanded] = React.useState(expanded);
  const icon = isExpanded ? toggleIcon.open : toggleIcon.closed;

  const handleOnToggle = React.useCallback((): void => {
    if (loading) return;
    if (canSelfToggle) setIsExpanded((oldVal) => !oldVal);

    if (is.func(onToggle)) onToggle();
  }, [onToggle, loading, canSelfToggle]);

  React.useEffect(() => {
    setIsExpanded(expanded);
  }, [expanded]);

  const addonWithWrapper = React.useMemo(() => {
    return (
      <Html.div
        testId={testId && `${testId}-addon`}
        className={[
          styles.addon,
          'text-right',
          'text-md-end',
          'col-12',
          'col-md-auto',
          !isExpanded && toggleable && 'd-none',
          'd-md-block',
          'pt-4',
          'pt-md-0',
          ...toArray(addonClassName),
        ]}
      >
        {is.string(addon) ? <Html.span typography="caption">{addon}</Html.span> : addon}
      </Html.div>
    );
  }, [testId, isExpanded, toggleable, addonClassName, addon]);

  const WrapWithShimmer = React.useCallback(
    (node: React.ReactElement) => {
      return <Shimmer loading={loading}>{node}</Shimmer>;
    },
    [loading]
  );

  return (
    <Html.div
      testId={testId}
      className={['rounded-1', styles.expandableRow, ...toArray(className)]}
      style={style}
      arias={rest}
    >
      <Html.div
        testId={testId && `${testId}-header`}
        className={[styles.header, toggleable && styles.headerToggleable, 'p-4']}
        onClick={handleOnToggle}
        aria-expanded={isExpanded}
      >
        <Html.div className="d-flex flex-wrap flex-md-nowrap">
          <Html.div className="d-flex flex-wrap flex-grow-1 w-100">
            <Html.div className="d-flex flex-nowrap w-100">
              <Html.div
                testId={testId && `${testId}-title`}
                className={[styles.title, 'col-md-auto flex-grow-1', !is.nullish(description) && 'mb-1']}
              >
                {WrapWithShimmer(
                  is.string(title) ? (
                    <Html.h6 typography="title2" fontWeight="bold" className="mb-0">
                      {title}
                    </Html.h6>
                  ) : (
                    title
                  )
                )}
              </Html.div>
              {toggleable && (
                <Html.div className={[styles.toggleIcon, 'col-md-auto me-n2']}>
                  <Icon name={icon} testId={testId && `${testId}-toggle-icon`} />
                </Html.div>
              )}
            </Html.div>
            {!is.nullish(description) && (
              <Html.div className="d-flex w-100 flex-wrap flex-md-nowrap">
                <Html.div
                  testId={testId && `${testId}-description`}
                  className={[styles.description, 'col-12 col-md-auto flex-grow-1 pe-6']}
                >
                  {WrapWithShimmer(
                    is.string(description) ? <Html.span typography="caption">{description}</Html.span> : description
                  )}
                </Html.div>
                {toggleable && !loading && addonWithWrapper}
              </Html.div>
            )}
          </Html.div>
          {!toggleable && !loading && <Html.div className="pt-md-2 ps-md-12">{addonWithWrapper}</Html.div>}
        </Html.div>
      </Html.div>
      {children && (
        <Html.div className={[styles.contentWrapper, isExpanded && styles.contentWrapperOpen]}>
          <Html.div testId={testId && `${testId}-content`} className={[styles.content]}>
            {children}
          </Html.div>
        </Html.div>
      )}
    </Html.div>
  );
};

export type { ExpandableRowElement };
export default withGATracking(ExpandableRow, { action: 'toggle', handler: 'onToggle' });
